import React, { FC, ReactNode, useMemo } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';

import { CoreCommonMessages, CoreUtils } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { CountryCode, PlayerPosition } from '~/core/enums';
import { getPlayerHeight, getPlayerWeight, parseValueFromLocalizedAttribute } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';

import { BioAndSocials } from '../../styles';
import { Player } from '../../types';

import { BioDetailsContainer, Label, PlayerInfo, SocialLinksContainer } from './styles';

type PlayerInfoAndSocialsProps = {
    player: Player;
};

type BioDivProps = {
    prefix: string;
    suffix: ReactNode;
};

const BioDiv: FC<BioDivProps> = ({ prefix, suffix }) => (
    <BioDetailsContainer>
        <Label>{`${prefix}: `}</Label>
        {suffix}
    </BioDetailsContainer>
);

// exporting these separately is necessary for the new medium width design
// TODO: re-integrate this when player socials links are actually passed up in the API
export const PlayerSocialsComponent: FC<PlayerInfoAndSocialsProps> = ({ player }) => (
    <SocialLinksContainer className="socials">
        <a href={player.shopLink}>
            <FormattedMessage {...CommonMessages.playerInfoDescriptions.long['shop']} />
        </a>
        &nbsp;|&nbsp;
        {/*TODO: are we including Twitter links (or are they "X linx" now)*/}
        {/*<a href={`https://twitter.com/${player.twitterLink}`}><FormattedMessage {...CommonMessages.playerInfoDescriptions.long['twitter']} /></a> |*/}{' '}
        <a href={player.watchLink}>
            <FormattedMessage {...CommonMessages.playerInfoDescriptions.long['watch']} />
        </a>
    </SocialLinksContainer>
);

export const PlayerInfoComponent: FC<PlayerInfoAndSocialsProps> = ({ player }) => {
    const intl = useIntl();

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const getDraftDetails = () => {
        const { draftDetails } = player;
        if (!draftDetails) {
            return (
                <FormattedMessage
                    description="Describes a player who did not get selected in the NHL draft"
                    defaultMessage="Undrafted"
                />
            );
        }
        return (
            <FormattedMessage
                description="Describes the details of a player's NHL draft experience"
                defaultMessage="{year}, {teamAbbrev} ({overallPick} overall), {roundNumber} round, {pickNumber} pick"
                values={{
                    year: draftDetails?.year,
                    teamAbbrev: draftDetails?.teamAbbrev,
                    overallPick: (
                        <FormattedMessage
                            {...CoreCommonMessages.misc.ordinal}
                            values={{ value: draftDetails?.overallPick }}
                        />
                    ),
                    roundNumber: (
                        <FormattedMessage
                            {...CoreCommonMessages.misc.ordinal}
                            values={{ value: draftDetails?.round }}
                        />
                    ),
                    pickNumber: (
                        <FormattedMessage
                            {...CoreCommonMessages.misc.ordinal}
                            values={{ value: draftDetails?.pickInRound }}
                        />
                    ),
                }}
            />
        );
    };

    const playerWeight = useMemo(
        () => getPlayerWeight(countryCode, player.weightInPounds, player.weightInKilograms),
        [countryCode, player.weightInPounds, player.weightInKilograms]
    );
    const playerHeight = useMemo(
        () => getPlayerHeight(countryCode, player.heightInInches),
        [countryCode, player.heightInInches]
    );

    const playerAgeString = useMemo(() => {
        const birthDate = DateTime.fromISO(player.birthDate);
        const localeBirthDate = birthDate.toLocaleString();

        // TODO: should probably return an unequivocal "this player is active or not" flag in the API
        if (player.currentTeamId !== undefined) {
            const today = DateTime.now();
            const difference = today.diff(birthDate, 'years').toObject();

            return intl.formatMessage(
                {
                    description: "A string giving a player's birthdate and age",
                    defaultMessage: '{date} (Age: {age})',
                },
                {
                    date: localeBirthDate,
                    age: difference.years !== undefined ? Math.floor(difference.years) : '',
                }
            );
        }

        return localeBirthDate;
    }, [intl, player]);

    const isNorthAmericaPlayer =
        player.birthCountry === CountryCode.Canada || player.birthCountry === CountryCode.UnitedStates;

    return (
        // NB: ID for automated testing purposes - do not remove
        <PlayerInfo id="player-info">
            <BioDiv
                prefix={intl.formatMessage(CommonMessages.playerInfoDescriptions.long['height'])}
                suffix={playerHeight}
            />
            <BioDiv
                prefix={intl.formatMessage(CommonMessages.playerInfoDescriptions.long['weight'])}
                suffix={playerWeight}
            />
            <BioDiv
                prefix={intl.formatMessage(CommonMessages.playerInfoDescriptions.long['born'])}
                suffix={playerAgeString}
            />
            <BioDiv
                prefix={intl.formatMessage(CommonMessages.playerInfoDescriptions.long['birthplace'])}
                suffix={
                    <FormattedMessage
                        description="Cell that shows the birthplace of a given player"
                        defaultMessage="<city>{birthCity}</city><stateProvince>, {birthStateProvince}</stateProvince><country>, {birthCountry}</country>"
                        values={{
                            birthCity: parseValueFromLocalizedAttribute(player.birthCity),
                            birthCountry: player.birthCountry,
                            birthStateProvince: parseValueFromLocalizedAttribute(player.birthStateProvince),
                            stateProvince: (text) => player.birthStateProvince && <span>{text}</span>,
                            city: (text) => player.birthCity && <span>{text}</span>,
                            country: (text) => !isNorthAmericaPlayer && player.birthCountry && <span>{text}</span>,
                        }}
                    />
                }
            />
            <BioDiv
                prefix={
                    player.position === PlayerPosition.goalie
                        ? intl.formatMessage(CommonMessages.playerInfoDescriptions.long['catches'])
                        : intl.formatMessage(CommonMessages.playerInfoDescriptions.long['shoots'])
                }
                suffix={
                    player.shootsCatches
                        ? CoreUtils.safeFormatMessage(
                              intl,
                              CommonMessages.playerInfoDescriptions.handedness.abbreviations[player.shootsCatches]
                          )
                        : intl.formatMessage(CommonMessages.statsDescriptions.symbols.missingInfo)
                }
            />
            <BioDiv
                prefix={intl.formatMessage(CommonMessages.playerInfoDescriptions.long['draft'])}
                suffix={getDraftDetails()}
            />
        </PlayerInfo>
    );
};

// used for small and wide widths
export const PlayerInfoAndSocials: FC<PlayerInfoAndSocialsProps> = ({ player }) => (
    <BioAndSocials>
        <PlayerInfoComponent player={player} />

        {/*
        Waiting to hear back if we're going to keep this or remove it
        <PlayerSocialsComponent player={player} />
        */}
    </BioAndSocials>
);
